import { BookCard, Box, useTheme, Typography, Divider } from '@hermes/web-components'

import BaseModal from '@components/BaseModal'
import BookImage from '@components/BookImage'
import { BookDto, MeiliConsolidatedBookDto } from '@hermes/domains'
import { useNavigation } from '@hooks/useNavigation'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { getVersionPriceLabel } from '@lib/utils/getVersionPriceLabel'

interface BookVersionsModalProps {
  isOpen: boolean
  onClose: () => void
  versions: MeiliConsolidatedBookDto['books'][number][] | BookDto['versions']
}

export const BookVersionsModal = ({ isOpen, onClose, versions }: BookVersionsModalProps) => {
  const { t } = useTranslateMessage()
  const theme = useTheme()
  const { navigate } = useNavigation()

  if (!versions) return null

  // Group books by whether they have creatorId
  const officialBooks = versions.filter((version) => !version.creatorId)
  const communityBooks = versions.filter((version) => version.creatorId)

  // Sort function with null checks for year
  const sortByYear = (a: (typeof versions)[0], b: (typeof versions)[0]) => {
    const yearA = a.year || 0
    const yearB = b.year || 0
    return yearB - yearA
  }

  const sortedOfficialBooks = officialBooks.sort(sortByYear)
  const sortedCommunityBooks = communityBooks.sort(sortByYear)

  const renderBookItem = (version: (typeof versions)[0]) => (
    <Box className="list-version-item" key={version.id}>
      <BookCard
        inline
        title={`${version.title}${version.subtitle ? `: ${version.subtitle}` : ''}`}
        cover={t({ id: version.format })}
        year={String(version.year)}
        edition={version.editionNumber ? `${version.editionNumber}. ${t({ id: 'edition' }).toLowerCase()}` : undefined}
        chipLabel={getVersionPriceLabel(version) || t({ id: 'common.sold_out', defaultMessage: 'Sold out' })}
        chipDisabled={getVersionPriceLabel(version) === null}
        globalAction={() => {
          navigate.productPage(version.slug)
          onClose()
        }}
        image={<BookImage src={version.smallImage} width={40} height={56} alt={version.title} />}
        hover
      />
    </Box>
  )

  return (
    <BaseModal isOpen={isOpen} width={438} onClose={onClose} title={t({ id: 'versions' })}>
      <Box
        sx={{
          padding: '0px 12px 12px',
          maxHeight: '70vh',
          overflowY: 'auto',
          '.list-version-item': {
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:last-child': {
              borderBottom: 'none'
            }
          }
        }}
      >
        {/* Official books */}
        {sortedOfficialBooks.map(renderBookItem)}

        {/* Divider and heading for community books */}
        {sortedCommunityBooks.length > 0 && (
          <>
            <Typography sx={{ paddingTop: '32px', paddingBottom: '12px' }} variant="body1">
              {t({ id: 'community_created', defaultMessage: 'Bøker opprettet av brukere' })}
            </Typography>
            <Divider />
          </>
        )}

        {/* Community books */}
        {sortedCommunityBooks.map(renderBookItem)}
      </Box>
    </BaseModal>
  )
}
